import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Tooltip, Dropdown } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupplierService } from '../../../Services/Manage/SupplierService';
import { PurchaseOrderService } from '../../../Services/Manage/PurchaseOrderService';
import { ExpenseServices } from '../../../Services/Manage/ExpenseServices';
import {
    EditOutlined, EyeOutlined, ShopOutlined, CheckCircleOutlined,
    CloseCircleOutlined, ShoppingCartOutlined, DollarOutlined,
    DownloadOutlined, PlusOutlined, FilterOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PERMISSION_LINK = '/inventory/suppliers';

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const CATEGORY_PALETTE = ["#1677ff", "#52c41a", "#722ed1", "#fa8c16", "#13c2c2", "#eb2f96", "#f5222d"];

const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#1677ff', marginTop: 3, cursor: 'pointer' }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const SuppliersDashboard = () => {
    const perm = getPermission(PERMISSION_LINK);
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allSuppliers, setAllSuppliers] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [loading, setLoading] = useState(false);

    const [allPOs, setAllPOs] = useState([]);
    const [paymentsByPo, setPaymentsByPo] = useState({});

    const getAllSuppliers = async () => {
        setLoading(true);
        try {
            const request = await SupplierService.getAllSupplier();
            setAllSuppliers(request.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getAllPOsWithPayments = async () => {
        try {
            const request = await PurchaseOrderService.getAllPO();
            const data = request.data || [];
            setAllPOs(data);

            const results = await Promise.all(
                data.map((po) => ExpenseServices.getPaymentByPoId(po.po_number).catch(() => ({ data: [] })))
            );
            const map = {};
            data.forEach((po, idx) => {
                const paid = (results[idx]?.data || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0);
                map[po.po_number] = paid;
            });
            setPaymentsByPo(map);
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getAllSuppliers();
        getAllPOsWithPayments();
    }, []);

    const visibleSuppliers = useMemo(
        () => perm.view_all ? allSuppliers : allSuppliers.filter((s) => String(s.added_by) === String(currentUserId)),
        [allSuppliers, perm.view_all, currentUserId]
    );

    const filteredSuppliers = useMemo(() => {
        let filtered = visibleSuppliers;

        if (searchText) {
            filtered = filtered.filter((s) =>
                Object.values(s).some((value) =>
                    value?.toString().toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }
        if (selectedCategory) filtered = filtered.filter((s) => s.category === selectedCategory);
        if (selectedLocation) filtered = filtered.filter((s) => s.city === selectedLocation);
        if (selectedStatus) filtered = filtered.filter((s) => (selectedStatus === 'Active' ? s.is_active : !s.is_active));

        return filtered;
    }, [visibleSuppliers, searchText, selectedCategory, selectedStatus, selectedLocation]);

    const totalSuppliers = visibleSuppliers.length;
    const activeSuppliers = useMemo(() => visibleSuppliers.filter((s) => s.is_active).length, [visibleSuppliers]);
    const inactiveSuppliers = useMemo(() => visibleSuppliers.filter((s) => !s.is_active).length, [visibleSuppliers]);

    // Payable to Suppliers = sum of (PO total - paid) across all non-cancelled POs,
    // plus each supplier's opening_balance carry-forward.
    const visiblePOs = useMemo(
        () => perm.view_all ? allPOs : allPOs.filter((p) => String(p.added_by) === String(currentUserId)),
        [allPOs, perm.view_all, currentUserId]
    );

    const payableToSuppliers = useMemo(() => {
        const poPending = visiblePOs.reduce((sum, po) => {
            if (po.status === "Cancelled") return sum;
            const paid = paymentsByPo[po.po_number] || 0;
            const pending = Number(po.total_amount || 0) - paid;
            return sum + (pending > 0 ? pending : 0);
        }, 0);
        const openingBalances = visibleSuppliers.reduce((s, r) => s + Number(r.opening_balance || 0), 0);
        return poPending + openingBalances;
    }, [visiblePOs, paymentsByPo, visibleSuppliers]);

    const recentPurchasesThisMonth = useMemo(() => {
        const now = new Date();
        const curMonth = now.getMonth();
        const curYear = now.getFullYear();
        return visiblePOs.reduce((sum, po) => {
            if (!po.po_date) return sum;
            const d = new Date(po.po_date);
            if (d.getMonth() === curMonth && d.getFullYear() === curYear) {
                return sum + Number(po.total_amount || 0);
            }
            return sum;
        }, 0);
    }, [visiblePOs]);

    const supplierSummaryData = useMemo(() => {
        const counts = { "Active": activeSuppliers, "Inactive": inactiveSuppliers };
        return Object.entries(counts)
            .filter(([, v]) => v > 0)
            .map(([name, value]) => ({ name, value, color: name === "Active" ? "#52c41a" : "#f5222d" }));
    }, [activeSuppliers, inactiveSuppliers]);
    const supplierSummaryTotal = supplierSummaryData.reduce((a, b) => a + b.value, 0);

    const topCategoriesData = useMemo(() => {
        const map = {};
        visibleSuppliers.forEach((s) => {
            const key = s.category || "Uncategorized";
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map)
            .map(([name, value], i) => ({ name, value, color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length] }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    }, [visibleSuppliers]);
    const topCategoriesTotal = topCategoriesData.reduce((a, b) => a + b.value, 0);

    const categoryOptions = [...new Set(visibleSuppliers.map((s) => s.category).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const locationOptions = [...new Set(visibleSuppliers.map((s) => s.city).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const statusOptions = [{ label: "Active", value: "Active" }, { label: "Inactive", value: "Inactive" }];

    const REPORT_COLUMNS = [
        { label: "Supplier Name" },
        { label: "Supplier Code" },
        { label: "Contact Person" },
        { label: "Phone" },
        { label: "Email" },
        { label: "Category" },
        { label: "Location" },
        { label: "Status" },
    ];

    const buildReportRows = () => filteredSuppliers.map((r) => ({
        "Supplier Name": r.supplier_name || "-",
        "Supplier Code": r.supplier_code || "-",
        "Contact Person": r.contact_person || "-",
        "Phone": r.mobile_number || "-",
        "Email": r.email_address || "-",
        "Category": r.category || "-",
        "Location": r.city || "-",
        "Status": r.is_active ? "Active" : "Inactive",
    }));

    const dateStamp = () => dayjs().format("YYYY-MM-DD");

    const handleExportExcel = () => {
        const rows = buildReportRows();
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Suppliers Report");
        XLSX.writeFile(wb, `Suppliers-Report-${dateStamp()}.xlsx`);
    };

    const handleExportPDF = () => {
        const rows = buildReportRows();
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Suppliers Report", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
        autoTable(doc, {
            startY: 26,
            head: [REPORT_COLUMNS.map((c) => c.label)],
            body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 88, 180] },
        });
        doc.save(`Suppliers-Report-${dateStamp()}.pdf`);
    };

    const exportMenuItems = [
        { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
        { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
    ];

    const columns = [
        { title: '#', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        {
            title: 'Supplier Name',
            dataIndex: 'supplier_name',
            key: 'supplier_name',
            render: (v, record) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>{record.supplier_code}</div>
                </div>
            ),
        },
        { title: 'Contact Person', dataIndex: 'contact_person', key: 'contact_person', render: (v) => v || '-' },
        { title: 'Phone', dataIndex: 'mobile_number', key: 'mobile_number', render: (v) => v || '-' },
        { title: 'Email', dataIndex: 'email_address', key: 'email_address', render: (v) => v || '-' },
        { title: 'Category', dataIndex: 'category', key: 'category', render: (v) => v || '-' },
        { title: 'Location', dataIndex: 'city', key: 'city', render: (v) => v || '-' },
        // {
        //     title: 'Outstanding (₹)',
        //     dataIndex: 'opening_balance',
        //     key: 'opening_balance',
        //     align: 'right',
        //     render: (v) => Number(v || 0).toLocaleString('en-IN'),
        // },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <span style={{
                    background: record.is_active ? '#52c41a1a' : '#f5222d1a',
                    color: record.is_active ? '#52c41a' : '#f5222d',
                    padding: '3px 10px', borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>
                    {record.is_active ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            title: 'Action',
            key: 'Action',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/suppliers/view-supplier', { state: { id: record.id, action: 'View', enableEdit: false, from: '/inventory/suppliers' } })}
                            icon={<EyeOutlined />} size="middle" disabled={!perm.read}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/suppliers/edit-supplier', { state: { id: record.id, action: 'Edit', enableEdit: true, from: '/inventory/suppliers' } })}
                            icon={<EditOutlined />} size="middle" disabled={!perm.edit}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Suppliers</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Suppliers</Typography.Text>
                </div>
          <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                    </Dropdown>
                    <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                        <Button
                            size="middle" type="primary" disabled={!perm.add}
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/inventory/suppliers/add-supplier', { state: { action: 'Add', enableEdit: true, from: '/inventory/suppliers' } })}
                            style={{ background: '#1677ff', borderColor: '#1677ff' }}
                        >
                            Add Supplier
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Total Suppliers" value={totalSuppliers} sub="View all suppliers" color="#722ed1" bg="#f5eeff" icon={<ShopOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Active Suppliers" value={activeSuppliers} sub="View active suppliers" color="#52c41a" bg="#f6ffed" icon={<CheckCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Inactive Suppliers" value={inactiveSuppliers} sub="View inactive suppliers" color="#fa8c16" bg="#fff7e6" icon={<CloseCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Recent Purchases (This Month)" value={`₹ ${recentPurchasesThisMonth.toLocaleString('en-IN')}`} sub="View purchase report" color="#1658b4" bg="#eef4ff" icon={<ShoppingCartOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <StatCard title="Payable to Suppliers" value={`₹ ${payableToSuppliers.toLocaleString('en-IN')}`} sub="View payables" color="#f5222d" bg="#fff1f0" icon={<DollarOutlined />} />
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '6px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={17}>
                        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} sm={12} md={6}>
                                    <Input
                                        allowClear
                                        placeholder="Search supplier name, email, phone..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Select allowClear placeholder="All Status" options={statusOptions}
                                        value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Select allowClear placeholder="All Categories" options={categoryOptions}
                                        value={selectedCategory} onChange={setSelectedCategory} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Select allowClear placeholder="All Locations" options={locationOptions}
                                        value={selectedLocation} onChange={setSelectedLocation} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            rowKey="id"
                            dataSource={filteredSuppliers.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))}
                            columns={columns}
                        />
                    </Col>

                    {/* Right sidebar — Supplier Summary & Top Categories only (no Top Suppliers by Purchase Value) */}
                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Supplier Summary</Typography.Text>
                            {supplierSummaryTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>🏭</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                    <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${supplierSummaryTotal ? Math.round((v / supplierSummaryTotal) * 100) : 0}%)`, n]} />
                                                <Pie data={supplierSummaryData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}>
                                                    {supplierSummaryData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{
                                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                            textAlign: 'center', pointerEvents: 'none',
                                        }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{supplierSummaryTotal}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total Suppliers</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {supplierSummaryData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({supplierSummaryTotal ? Math.round((e.value / supplierSummaryTotal) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Top Categories</Typography.Text>
                            {topCategoriesTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>🗂️</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ marginTop: 12 }}>
                                    {topCategoriesData.map((c, i) => {
                                        const pct = topCategoriesTotal ? Math.round((c.value / topCategoriesTotal) * 100) : 0;
                                        return (
                                            <div key={i} style={{ marginBottom: 10 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                                    <span style={{ color: '#333' }}>{c.name}</span>
                                                    <span style={{ fontWeight: 700, color: '#333' }}>{c.value} ({pct}%)</span>
                                                </div>
                                                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                                                    <div style={{ width: `${pct}%`, height: '100%', background: c.color, borderRadius: 3 }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default SuppliersDashboard;